import { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Center,
  Group,
  Paper,
  Skeleton,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import dayjs from "dayjs";

import {
  BillingReportCoreFilters,
  DEFAULT_BILLING_REPORT_ACCOUNT_TYPE,
} from "../../components/reports/BillingReportCoreFilters";
import {
  DateRange,
  getLastMonthRange,
} from "../../components/reports/ReportDateRangePicker";
import { ReportFileExportButton } from "../../components/reports/ReportFileExportButton";
import { useSpecimenLogReport } from "../../hooks/useReports";
import { useLabTodayDateOnly } from "../../hooks/useLabTodayDateOnly";
import { formatLabDateTime } from "../../utils/labTime";
import {
  BillingReportAccountType,
  BillingReportDateBasis,
  reportsService,
} from "../../services/reports.service";
import { billingAccountTypeLabel } from "../internal/billingReportLabels";

const DEFAULT_RANGE = getLastMonthRange();

export function SpecimenLogReportContent() {
  const labToday = useLabTodayDateOnly();
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_RANGE);
  const [dateBasis, setDateBasis] =
    useState<BillingReportDateBasis>("first_reported");
  const [accountType, setAccountType] = useState<BillingReportAccountType>(
    DEFAULT_BILLING_REPORT_ACCOUNT_TYPE
  );

  const params = useMemo(
    () => ({
      startDate: dayjs(dateRange.startDate).format("YYYY-MM-DD"),
      endDate: dayjs(dateRange.endDate).format("YYYY-MM-DD"),
      dateBasis,
      accountTypes: [accountType],
    }),
    [accountType, dateBasis, dateRange]
  );
  const { data, isLoading, isFetching, isError } = useSpecimenLogReport(params);
  const reportIsLoading = isLoading || isFetching;

  const handleDateBasisChange = useCallback((value: string | null) => {
    if (value) setDateBasis(value as BillingReportDateBasis);
  }, []);
  const handleAccountTypeChange = useCallback((value: string | null) => {
    if (value) setAccountType(value as BillingReportAccountType);
  }, []);
  const handleDateRangeChange = useCallback((value: DateRange) => {
    setDateRange(value);
  }, []);
  const exportParams = useMemo(
    () => ({ ...params, reportRunId: data?.reportRunId }),
    [data?.reportRunId, params]
  );
  const handleExcelExport = useCallback(
    () => reportsService.exportSpecimenLogExcel(exportParams),
    [exportParams]
  );
  const handlePdfExport = useCallback(
    () => reportsService.exportSpecimenLogPdf(exportParams),
    [exportParams]
  );

  return (
    <Stack gap="lg">
      <Paper p="md" withBorder>
        <Group justify="space-between" align="flex-end" wrap="wrap">
          <Group align="flex-end" wrap="wrap" gap="md">
            <BillingReportCoreFilters
              dateBasis={dateBasis}
              accountType={accountType}
              dateRange={dateRange}
              onDateBasisChange={handleDateBasisChange}
              onAccountTypeChange={handleAccountTypeChange}
              onDateRangeChange={handleDateRangeChange}
            />
          </Group>
          <Group gap="xs">
            <ReportFileExportButton
              label="Export Excel"
              filename={`specimen-log-${labToday}.xlsx`}
              fetchFile={handleExcelExport}
              disabled={!data || reportIsLoading || isError}
            />
            <ReportFileExportButton
              label="Export PDF"
              filename={`specimen-log-${labToday}.pdf`}
              fetchFile={handlePdfExport}
              disabled={!data || reportIsLoading || isError}
            />
          </Group>
        </Group>
      </Paper>

      {!isError && !reportIsLoading && (data?.unassignedCaseCount ?? 0) > 0 && (
        <Alert
          color="yellow"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          title="Unassigned billing accounts"
          role="alert"
        >
          {data?.unassignedCaseCount} qualifying case
          {data?.unassignedCaseCount === 1 ? "" : "s"} could not be assigned to
          the selected account type. They remain visible under Unassigned for
          reconciliation.
          <br />
          Cases:{" "}
          {data?.reconciliationCases
            .filter((reportCase) =>
              reportCase.issues.includes("Unassigned billing account")
            )
            .map((reportCase) => reportCase.caseNumber)
            .join(", ")}
        </Alert>
      )}

      {isError && (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={16} />}
          title="Report could not be verified"
          role="alert"
        >
          No specimen totals are shown or exportable because the report failed
          its data checks. Retry, or investigate the case data before billing.
        </Alert>
      )}

      {data && !isError && !reportIsLoading && (
        <Group grow>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Total Cases
            </Text>
            <Text size="xl" fw={700}>
              {data.totalCases}
            </Text>
          </Paper>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
              Total Parts
            </Text>
            <Text size="xl" fw={700}>
              {data.totalParts}
            </Text>
            <Text size="xs" c="dimmed">
              Generated {formatLabDateTime(data.generatedAt)}
            </Text>
          </Paper>
        </Group>
      )}

      {reportIsLoading ? (
        <Stack gap="xs">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} h={36} />
          ))}
        </Stack>
      ) : isError ? null : !data || data.rows.length === 0 ? (
        <Center py="xl">
          <Text c="dimmed">No qualifying cases for the selected period.</Text>
        </Center>
      ) : (
        <Table striped highlightOnHover withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Account Type</Table.Th>
              <Table.Th>Primary Account Name</Table.Th>
              <Table.Th ta="right">Case Count</Table.Th>
              <Table.Th ta="right">Part Count</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.rows.map((row) => (
              <Table.Tr key={`${row.accountType}:${row.accountId}`}>
                <Table.Td>{billingAccountTypeLabel(row.accountType)}</Table.Td>
                <Table.Td>{row.accountName}</Table.Td>
                <Table.Td ta="right">{row.caseCount}</Table.Td>
                <Table.Td ta="right">{row.partCount}</Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr fw={700}>
              <Table.Td />
              <Table.Td>Totals</Table.Td>
              <Table.Td ta="right">{data.totalCases}</Table.Td>
              <Table.Td ta="right">{data.totalParts}</Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}
